// RITA-1585 — Gerüst der PRM-Handbücher: Handbücher mit ihren Kapiteln, noch ohne Text. Der Text kommt in einem
// eigenen Schritt (RITA-901) und liegt dann im PRM; Confluence bleibt die zweite Ablage. Die Kachel „Einstellungen ›
// Handbuch“ ist auf mehrere Handbücher angelegt (Julia, 31.10.2026): heute nur „Vertragserstellung“ (Kapitel laut
// Entwurf RITA-1551 vom 30.10.2026), später die anderen PRM-Handbücher aus Confluence (Anlegen eines Beraters,
// Berater kündigen, Buchhaltung …) mit eigenen Kapiteln.

export interface Handbuchkapitel {
  /** Stabiler Schlüssel für Adresse (`?kapitel=…`) und localStorage; ändert sich nicht mit dem Titel. */
  id: string;
  nummer: number;
  titel: string;
}

export interface Handbuch {
  id: string;
  titel: string;
  kapitel: readonly Handbuchkapitel[];
}

export const HANDBUCH_VERTRAGSERSTELLUNG_ID = 'vertragserstellung';

export const HANDBUECHER: readonly Handbuch[] = [
  {
    id: HANDBUCH_VERTRAGSERSTELLUNG_ID,
    titel: 'Vertragserstellung',
    kapitel: [
      { id: 'vorbereitungen', nummer: 1, titel: 'Vor dem ersten Vertrag: Vorbereitungen' },
      { id: 'vorlagen-anlegen', nummer: 2, titel: 'Vertragsvorlagen anlegen und zuordnen' },
      { id: 'vorlagen-bearbeiten', nummer: 3, titel: 'Vertragsvorlagen bearbeiten und prüfen' },
      { id: 'vertrag-erstellen', nummer: 4, titel: 'Beim Berater einen Vertrag erstellen' },
      { id: 'vertrag-bearbeiten', nummer: 5, titel: 'Vertrag bearbeiten und als Entwurf speichern' },
      { id: 'drucken-ablegen', nummer: 6, titel: 'Drucken, ablegen und später wiederfinden' },
      {
        id: 'unterschrift-erfassen',
        nummer: 7,
        titel: 'Unterschriebenen Vertrag erfassen und Rücksendung vorbereiten',
      },
      { id: 'platzhalter', nummer: 8, titel: 'Platzhalter erklärt' },
      { id: 'haeufige-fragen', nummer: 9, titel: 'Häufige Fragen und Hilfe bei Meldungen' },
    ],
  },
];

/** Ein Kapitel eines Handbuchs, eindeutig über beide Schlüssel. */
export interface Kapitelwahl {
  handbuchId: string;
  kapitelId: string;
}

/**
 * Seiten, die das Handbuch zeigen. Jede merkt sich ihr Kapitel für sich (Entscheidung Julia 31.10.2026): Der
 * Vorlagen-Editor öffnet beim ersten Mal Kapitel 3, der Vertrag am Berater Kapitel 5, die Kachel Kapitel 1.
 */
export type Handbuchseite = 'vorlagenEditor' | 'vertragBearbeiten' | 'kachel';

export const STARTKAPITEL: Readonly<Record<Handbuchseite, Kapitelwahl>> = {
  vorlagenEditor: { handbuchId: HANDBUCH_VERTRAGSERSTELLUNG_ID, kapitelId: 'vorlagen-bearbeiten' },
  vertragBearbeiten: { handbuchId: HANDBUCH_VERTRAGSERSTELLUNG_ID, kapitelId: 'vertrag-bearbeiten' },
  kachel: { handbuchId: HANDBUCH_VERTRAGSERSTELLUNG_ID, kapitelId: 'vorbereitungen' },
};

export function handbuchFinden(handbuchId: string, handbuecher: readonly Handbuch[] = HANDBUECHER): Handbuch | null {
  return handbuecher.find((handbuch) => handbuch.id === handbuchId) ?? null;
}

export function kapitelFinden(
  wahl: Kapitelwahl,
  handbuecher: readonly Handbuch[] = HANDBUECHER,
): { handbuch: Handbuch; kapitel: Handbuchkapitel } | null {
  const handbuch = handbuchFinden(wahl.handbuchId, handbuecher);
  const kapitel = handbuch?.kapitel.find((eintrag) => eintrag.id === wahl.kapitelId);
  return handbuch && kapitel ? { handbuch, kapitel } : null;
}

/** Prüft fremde Werte (Adresse, localStorage) und liefert nur eine Wahl, die es wirklich gibt. */
export function gueltigeKapitelwahl(
  handbuchId: unknown,
  kapitelId: unknown,
  handbuecher: readonly Handbuch[] = HANDBUECHER,
): Kapitelwahl | null {
  if (typeof handbuchId !== 'string' || typeof kapitelId !== 'string') return null;
  const wahl = { handbuchId, kapitelId };
  return kapitelFinden(wahl, handbuecher) ? wahl : null;
}

/** Anzeige „3. Vertragsvorlagen bearbeiten und prüfen“ – so steht es in Auswahl, Überschrift und Treffern. */
export function kapitelBezeichnung(kapitel: Handbuchkapitel): string {
  return `${kapitel.nummer}. ${kapitel.titel}`;
}

export interface Suchtreffer {
  handbuch: Handbuch;
  kapitel: Handbuchkapitel;
}

/**
 * Suche über die Kapitel der übergebenen Handbücher. Solange kein Text vorliegt, findet sie nur Kapitelnamen
 * (Groß-/Kleinschreibung egal); mit dem Text (RITA-901) kommt der Inhalt dazu. Leere Eingabe: keine Treffer.
 */
export function handbuchSuchen(begriff: string, handbuecher: readonly Handbuch[] = HANDBUECHER): Suchtreffer[] {
  const gesucht = begriff.trim().toLocaleLowerCase('de-DE');
  if (!gesucht) return [];
  return handbuecher.flatMap((handbuch) =>
    handbuch.kapitel
      .filter((kapitel) => kapitelBezeichnung(kapitel).toLocaleLowerCase('de-DE').includes(gesucht))
      .map((kapitel) => ({ handbuch, kapitel })),
  );
}
